import React from 'react';
import { useTranslation } from 'react-i18next';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';

import { useAppSelector } from '@/store';
import { resolveMainWalletForCurrency } from '@/store/slices/wallet';
import { stripeLocale } from '@/utils/stripe-locale';

import { Button, Icon } from '../../../../components';
import { COLORS } from '../../../../theme/tokens';
import { getStripe, useStripeAppearance } from '../../../configurator-v3/data/payments';
import { currencySymbol, formatAmountWithCode } from '../../../../utils/money';
import { DEFAULT_CURRENCY, DEFAULT_LANG } from '../../../../utils/catalog';
import { Sheet } from '@/nova/components/Sheet';
import {
  HeaderId,
  IconTile,
  IconArt,
  HeaderText,
  NameRow,
  Name,
  TierPill,
  PriceRow,
  Price,
  DiscountCol,
  Savings,
  PrevPrice,
  Subtitle,
  OptionCard,
  OptionRow,
  Radio,
  OptionText,
  OptionLabel,
  OptionHint,
  OptionNote,
  OptionDrawer,
  OptionDrawerInner,
  StripeArea,
  StripeSectionLabel,
  CardStatusNote,
  TotalRow,
  TotalLabel,
  TotalCol,
  TotalPrice,
  Footnote,
} from './PaymentModal.styles';

export type PaymentMethodId = 'wallet' | 'card';

/**
 * Card-checkout phase (card path only). The Stripe intent is created LAZILY on the first
 * "Pay" click — NOT at modal open — so `activate-item` mints the activation invoice (and its
 * PaymentIntent) FIRST and Stripe binds to that REAL intent, never an orphaned deferred one.
 *   idle → preparing (mint + fetch the secret) → ready (bind Elements, collect the card)
 * `error` is the recoverable branch off `preparing` (couldn't create the intent).
 */
type CardPhase = 'idle' | 'preparing' | 'ready' | 'error';

export type PaymentModalProps = {
  open: boolean;
  /** Feature artwork URL. */
  image: string;
  /** Feature display name. */
  name: string;
  /** Selected tier/plan display label ("Pro"). */
  tierName: string;
  /** Monthly amount of the selected plan ("60"). */
  amount: string;
  /** 3-letter currency code ("EUR"). */
  currency: string;
  /** Struck-through previous price ("78 EUR per month"); null = no discount. */
  prevPrice: string | null;
  /** Discount note ("Save 30%"); null = no discount. */
  savings: string | null;
  /** Wallet settlement in flight (parent-owned) — disables the CTA and blocks a double submit. */
  busy?: boolean;
  onClose: () => void;
  /** Wallet path: the parent activates the feature and settles the minted invoice from the MAIN
   *  balance, then refetches + closes. */
  onPayWithWallet: () => void;
  /** Card path: mint the activation invoice (`POST /activate-item`) and resolve its Stripe
   *  PaymentIntent client secret. Called on the first "Pay" click, BEFORE any Stripe UI mounts,
   *  so the card form binds to the real invoice intent (no orphaned deferred payment). Rejects
   *  when activation can't produce a payable card intent. */
  createCardIntent: () => Promise<string>;
  /** Card path: fired AFTER the in-modal Stripe confirmation succeeds — the parent refetches + closes. */
  onCardPaid: () => void;
};

/**
 * The Stripe Payment Element + confirm logic, mounted inside <Elements> bound to the invoice's
 * PaymentIntent clientSecret (intent-first — the same wiring the wallet-deposit + configurator
 * checkouts use). Registers its confirm into `confirmRef` so the modal's Pay button (outside
 * <Elements>) can trigger it, and reports confirming/error/paid back up. On success the invoice
 * is settled server-side (webhook) and the parent refetches.
 */
function CardConfirm(p: {
  clientSecret: string;
  confirmRef: React.MutableRefObject<(() => void) | null>;
  setConfirming: (value: boolean) => void;
  setError: (value: string | null) => void;
  onPaid: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const { clientSecret, confirmRef, setConfirming, setError, onPaid } = p;
  // Synchronous re-entry guard: the Pay button's disabled state only reflects `confirming` on the
  // NEXT render, so a fast second click could fire a second confirmPayment on the same intent
  // before then. This ref bails immediately.
  const inFlight = React.useRef(false);

  React.useEffect(() => {
    confirmRef.current = async (): Promise<void> => {
      if (!stripe || !elements || inFlight.current) return;
      inFlight.current = true;
      setConfirming(true);
      setError(null);
      const fail = (message: string | undefined): void => {
        setError(message ?? t('Payment failed. Please try again.'));
        setConfirming(false);
        inFlight.current = false;
      };
      const submitResult = await elements.submit();
      if (submitResult.error) {
        fail(submitResult.error.message);
        return;
      }
      const result = await stripe.confirmPayment({
        elements,
        clientSecret,
        redirect: 'if_required',
        confirmParams: { return_url: window.location.origin + window.location.pathname },
      });
      if (result.error) {
        fail(result.error.message);
        return;
      }
      // Charged. Clear the in-modal confirming state BEFORE handing off so a slow (or failing)
      // parent refetch can't leave the CTA wedged on "Processing…". The parent closes the modal.
      setConfirming(false);
      inFlight.current = false;
      onPaid();
    };
    return () => {
      confirmRef.current = null;
    };
  }, [stripe, elements, clientSecret, confirmRef, setConfirming, setError, onPaid, t]);

  return <PaymentElement options={{ layout: { type: 'accordion', defaultCollapsed: false } }} />;
}

/**
 * "Pay to activate feature" modal (Figma: Modal / Pay to activate feature).
 * Two payment options: the casino wallet, and "credit card or other options". The due-today
 * figure is the plan price pro-rated for the remainder of the current month.
 *
 * The card path is INTENT-FIRST and lazy: nothing Stripe mounts until the first "Pay" click,
 * which runs `activate-item` to mint the invoice + its PaymentIntent, then binds the card form
 * to that real intent (see {@link CardPhase} / {@link CardConfirm}). This avoids the orphaned
 * deferred PaymentIntent + unpaid invoice the old deferred wiring produced.
 */
export const PaymentModal = ({
  open,
  image,
  name,
  tierName,
  amount,
  currency,
  prevPrice,
  savings,
  busy = false,
  onClose,
  onPayWithWallet,
  createCardIntent,
  onCardPaid,
}: PaymentModalProps): React.ReactElement | null => {
  const { t, i18n } = useTranslation();
  const [method, setMethod] = React.useState<PaymentMethodId>('wallet');

  // Card-path state — the Stripe intent is created lazily on the first Pay click (see CardPhase).
  const [cardPhase, setCardPhase] = React.useState<CardPhase>('idle');
  const [cardSecret, setCardSecret] = React.useState<string | null>(null);
  const [cardError, setCardError] = React.useState<string | null>(null);
  const [confirming, setConfirming] = React.useState(false);
  // Set by CardConfirm (inside <Elements>) so the Pay button can trigger the confirm.
  const confirmRef = React.useRef<(() => void) | null>(null);

  // Same Stripe skin as the configurator-v3 checkout and the wallet deposit flow,
  // resolved to the active studio theme (re-themes live on a light⇄dark switch). A
  // hook, so it must run unconditionally before the `!open` early return below.
  const appearance = useStripeAppearance();

  // The wallet option pays from the MAIN wallet in the SELECTED CASINO's currency (the tier's
  // `currency`), so the balance reads in the same currency as the price — falling back to the
  // primary MAIN wallet when the owner has none in that currency. It's fetched by the
  // always-mounted TopBar, so it's loaded by the time this modal opens; while it isn't, we drop
  // the balance from the label rather than show a misleading "$0".
  const ownerWallet = useAppSelector((state) => state.ownerWallet ?? null);
  const walletView = resolveMainWalletForCurrency(ownerWallet, currency || DEFAULT_CURRENCY);
  // The owner's available MAIN-wallet balance (major units), or null until the always-mounted
  // TopBar has loaded it. Drives both the balance label and the insufficient-funds guard below.
  const walletAvailable = walletView.balance;
  const walletBalance =
    walletAvailable !== null
      ? `${currencySymbol(walletView.currency)}${formatAmountWithCode(walletAvailable, walletView.currency)}`
      : null;

  // The component stays mounted while closed — reset so every open starts clean (wallet option,
  // no stale card intent / phase / error).
  React.useEffect(() => {
    if (!open) {
      setMethod('wallet');
      setCardPhase('idle');
      setCardSecret(null);
      setCardError(null);
      setConfirming(false);
    }
  }, [open]);

  const monthly = Number(amount) || 0;
  const currencyCode = currency || DEFAULT_CURRENCY;
  // Symbol + amount + ISO code, e.g. "$59.99 USD" — matches the tier cards + configurator.
  const money = (value: number): string => `${currencySymbol(currencyCode)}${formatAmountWithCode(value, currencyCode)}`;
  const monthlyText = money(monthly);

  // The BACKEND is the sole source of truth for the amount: activation charges the full monthly
  // price (no client-side proration), so "due today" == the monthly price. The card path confirms
  // the real invoice PaymentIntent and the wallet path settles the real invoice, so what we show
  // here always matches what's charged.
  const now = new Date();
  const nextBillingDate = new Date(now.getFullYear(), now.getMonth() + 1, 1).toLocaleDateString(
    i18n.language || DEFAULT_LANG,
    { month: 'long', day: 'numeric' },
  );

  // When the balance can't cover the charge, the wallet option is unusable: disable it (with a
  // note) and force the card path. While the balance is still loading (null) we don't block.
  const walletShort = walletAvailable !== null && Math.round(walletAvailable * 100) < Math.round(monthly * 100);
  // The wallet is disabled when funds are short, so the effective (selected) method is
  // forced to card in that case, whatever the last user choice was. Purely derived —
  // no flash on open, and it self-corrects if the balance loads/changes late.
  const selectedMethod: PaymentMethodId = walletShort ? 'card' : method;

  const onSelectWallet = React.useCallback(() => setMethod('wallet'), []);
  const onSelectCard = React.useCallback(() => setMethod('card'), []);

  // First card "Pay" click: mint the activation invoice + fetch its PaymentIntent secret, THEN
  // reveal the Stripe form bound to that real intent.
  const startCardCheckout = React.useCallback(async () => {
    setCardPhase('preparing');
    setCardError(null);
    try {
      const secret = await createCardIntent();
      setCardSecret(secret);
      setCardPhase('ready');
    } catch (e) {
      const message = (e as { error?: string; message?: string } | undefined)?.error ?? (e as { message?: string } | undefined)?.message;
      setCardError(message ?? t('Could not start checkout. Please try again.'));
      setCardPhase('error');
    }
  }, [createCardIntent, t]);

  const onPayClick = React.useCallback(() => {
    // Don't re-enter while a settle / mint / confirm is already in flight.
    if (busy || confirming || cardPhase === 'preparing') return;
    if (selectedMethod === 'wallet') {
      onPayWithWallet();
      return;
    }
    // Card: once the intent is ready, the same button confirms the entered card; before that
    // (idle / after an error) it mints the invoice and loads the form.
    if (cardPhase === 'ready') {
      void confirmRef.current?.();
      return;
    }
    void startCardCheckout();
  }, [busy, confirming, cardPhase, selectedMethod, onPayWithWallet, startCardCheckout]);

  // Block closing while a mint or card confirm is in flight so an in-progress charge isn't
  // abandoned mid-way (the parent also guards its own wallet-settle via `busy`).
  //
  // Told to the Overlay as `busy` rather than vetoed here: refusing the call the exit has
  // already scheduled would leave the sheet mounted and off screen, its backdrop swallowing
  // every click. The Overlay refuses the dismissal itself, before anything starts moving.
  const holdOpen = confirming || cardPhase === 'preparing';

  if (!open) {
    return null;
  }

  const payDisabled = busy || confirming || cardPhase === 'preparing';
  let payLabel: string;
  if (busy) {
    payLabel = t('Activating…');
  } else if (cardPhase === 'preparing') {
    payLabel = t('Starting secure checkout…');
  } else if (confirming) {
    payLabel = t('Processing…');
  } else if (selectedMethod === 'card' && cardPhase === 'error') {
    payLabel = t('Try again');
  } else {
    payLabel = t('Pay {{amount}} to activate', { amount: monthlyText });
  }

  return (
    <Sheet
      // The visible feature name labels the dialog (aria only — the rich header
      // renders the name visually). Reuses the existing "{{name}}" i18n key.
      ariaLabel="{{name}}"
      ariaLabelPlaceholders={{ name }}
      // The checkout (two options + card form + total + CTA) is tall: let the sheet use the full
      // available viewport height instead of the 720px default, so it only scrolls internally
      // when it truly runs out of room.
      maxHeight="100%"
      onClose={onClose}
      busy={holdOpen}
      header={
        <HeaderId>
          <IconTile>
            <IconArt src={image} alt="" />
          </IconTile>
          <HeaderText>
            <NameRow>
              <Name text={name} tag="span" />
              <TierPill text={tierName} tag="span" />
            </NameRow>
            <PriceRow>
              <Price
                text="{{symbol}}{{price}} /month"
                placeholders={{ symbol: currencySymbol(currencyCode), price: formatAmountWithCode(monthly, currencyCode) }}
                tag="span"
              />
              {prevPrice || savings ? (
                <DiscountCol>
                  {savings ? <Savings text={savings} tag="span" /> : null}
                  {prevPrice ? <PrevPrice text={prevPrice} tag="span" /> : null}
                </DiscountCol>
              ) : null}
            </PriceRow>
          </HeaderText>
        </HeaderId>
      }
    >
      <Subtitle text="Use one of the following options to activate your monthly subscription" tag="span" />

      <OptionCard>
        <OptionRow type="button" onClick={onSelectWallet} disabled={walletShort} $disabled={walletShort}>
          <Radio $selected={selectedMethod === 'wallet'}>
            {selectedMethod === 'wallet' ? <Icon name="check" size={10} color={COLORS.onAccent} /> : null}
          </Radio>
          <OptionText>
            {walletBalance ? (
              <OptionLabel
                text="Your wallet · ({{balance}})"
                placeholders={{ balance: walletBalance }}
                tag="span"
              />
            ) : (
              <OptionLabel text="Your wallet" tag="span" />
            )}
            <OptionHint
              text="Every month we will deduct {{price}} from your balance"
              placeholders={{ price: monthlyText }}
              tag="span"
            />
          </OptionText>
        </OptionRow>
        {walletShort ? <OptionNote text="Not enough balance to pay from your wallet." tag="span" /> : null}
      </OptionCard>

      <OptionCard>
        <OptionRow type="button" onClick={onSelectCard}>
          <Radio $selected={selectedMethod === 'card'}>
            {selectedMethod === 'card' ? <Icon name="check" size={10} color={COLORS.onAccent} /> : null}
          </Radio>
          <OptionText>
            <OptionLabel text="Credit card or other options" tag="span" />
            <OptionHint text="Pick your preferred method" tag="span" />
          </OptionText>
        </OptionRow>
        {/* The drawer only expands once the intent is being prepared / is ready — the Stripe form
            never mounts (and no intent is created) until the first "Pay" click. */}
        <OptionDrawer $open={selectedMethod === 'card' && cardPhase !== 'idle'} aria-hidden={selectedMethod !== 'card'}>
          <OptionDrawerInner>
            <StripeArea>
              {cardPhase === 'preparing' ? <CardStatusNote text="Preparing secure checkout…" tag="span" /> : null}
              {cardPhase === 'ready' && cardSecret ? (
                <Elements
                  // `locale` is NOT mutable after mount, unlike `appearance` — keying on it
                  // remounts Elements on a language switch instead of leaving Stripe stuck in
                  // the language the modal opened in.
                  key={stripeLocale(i18n.language)}
                  stripe={getStripe()}
                  options={{
                    clientSecret: cardSecret,
                    appearance,
                    // Stripe renders its own copy (field labels, validation errors) and defaults
                    // to the BROWSER's language rather than ours unless told explicitly.
                    locale: stripeLocale(i18n.language),
                  }}
                >
                  <StripeSectionLabel text="Payment method" tag="span" />
                  <CardConfirm
                    clientSecret={cardSecret}
                    confirmRef={confirmRef}
                    setConfirming={setConfirming}
                    setError={setCardError}
                    onPaid={onCardPaid}
                  />
                </Elements>
              ) : null}
              {cardError ? <CardStatusNote text={cardError} tag="span" /> : null}
            </StripeArea>
          </OptionDrawerInner>
        </OptionDrawer>
      </OptionCard>

      <TotalRow>
        <TotalLabel text="Total due today" tag="span" />
        <TotalCol>
          <TotalPrice>{monthlyText}</TotalPrice>
        </TotalCol>
      </TotalRow>

      <Button variant="primary" fullWidth disabled={payDisabled} label={payLabel} onClick={onPayClick} />

      <Footnote
        text="Then {{price}} each month starting on {{date}}."
        placeholders={{ price: monthlyText, date: nextBillingDate }}
        tag="span"
      />
    </Sheet>
  );
};

export default PaymentModal;
